import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useHead } from "@unhead/vue";
import {
  getLocaleFromRoute,
  getAlternateUrl,
  getBaseRouteName,
} from "@/router";
import { getCapitalBySlug } from "@/utils/provinceMapping";

const BASE_URL = "https://calidadtelecos.cnmc.gob.es";

function generateFallbackTitle(titleKey) {
  if (!titleKey) return "CNMC";
  const keyName = titleKey.split(".").pop();
  return keyName
    .replaceAll(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

export function useSeoHead() {
  const route = useRoute();
  const { t } = useI18n({ useScope: "global" });

  const locale = computed(() => getLocaleFromRoute(route));

  const isErrorPage = computed(() => route.meta.isErrorPage === true);
  const isHiddenPage = computed(() => route.meta.isHidden === true);

  // Caso especial: estadísticas con capital de provincia
  const isEstadisticasUbicacion = computed(() => {
    const baseName = getBaseRouteName(route.name);
    return baseName === "estadisticasUbicacion";
  });

  const capitalData = computed(() => {
    const ubicacion = route.params.ubicacion;
    return ubicacion ? getCapitalBySlug(ubicacion) : null;
  });

  const isCapital = computed(() => capitalData.value !== null);

  const isExcludedFromSitemap = computed(() => {
    if (route.meta.excludeFromSitemap !== true) return false;
    // Capital de provincia → permitir indexación
    if (isEstadisticasUbicacion.value && isCapital.value) return false;
    return true;
  });

  const shouldIndex = computed(
    () => !isErrorPage.value && !isHiddenPage.value && !isExcludedFromSitemap.value
  );

  // URLs alternas para hreflang
  const esUrl = computed(() =>
    shouldIndex.value ? getAlternateUrl(route, "es", false) : null
  );
  const enUrl = computed(() =>
    shouldIndex.value ? getAlternateUrl(route, "en", false) : null
  );

  // Canonical URL
  const canonicalUrl = computed(() =>
    shouldIndex.value ? `${BASE_URL}${route.path}` : null
  );

  // Título y descripción
  const pageTitle = computed(() => {
    // Caso especial: capital de provincia
    if (isEstadisticasUbicacion.value && isCapital.value) {
      const cityName = capitalData.value.nombre;
      return locale.value === "en"
        ? `Internet coverage statistics in ${cityName}`
        : `Estadísticas de cobertura de internet en ${cityName}`;
    }

    // Caso normal: traducciones (forzar locale correcto para SSG)
    const titleKey = route.meta.titleKey || "seo.titles.home";
    const loc = locale.value;
    const translated = t(titleKey, {}, { locale: loc });
    return translated === titleKey ? generateFallbackTitle(titleKey) : translated;
  });

  const pageDescription = computed(() => {
    // Caso especial: capital de provincia
    if (isEstadisticasUbicacion.value && isCapital.value) {
      const cityName = capitalData.value.nombre;
      return locale.value === "en"
        ? `Check mobile and fixed internet coverage statistics in ${cityName}. Download speed, upload speed, latency and signal quality data from CNMC.`
        : `Consulta las estadísticas de cobertura de internet móvil y fijo en ${cityName}. Datos de velocidad de descarga, subida, latencia y calidad de señal de la CNMC.`;
    }

    // Caso normal (forzar locale correcto para SSG)
    const titleKey = route.meta.titleKey || "seo.titles.home";
    const descriptionKey = titleKey.replace("seo.titles", "seo.descriptions");
    const loc = locale.value;
    const translated = t(descriptionKey, {}, { locale: loc });

    if (translated === descriptionKey) {
      return loc === "en"
        ? "Official CNMC speed test. Measure your internet connection quality."
        : "Test de velocidad oficial de la CNMC. Mide la calidad de tu conexión a internet.";
    }

    return translated;
  });

  const fullTitle = computed(() => `${pageTitle.value} - CNMC`);

  const ogLocale = computed(() =>
    locale.value === "en" ? "en_GB" : "es_ES"
  );

  const ogUrl = computed(() =>
    shouldIndex.value ? `${BASE_URL}${route.path}` : BASE_URL
  );

  const ogImage = computed(() => `${BASE_URL}/og-image.png`);

  const ogLocaleAlternate = computed(() =>
    locale.value === "en" ? "es_ES" : "en_GB"
  );

  const robotsContent = computed(() =>
    shouldIndex.value ? "index, follow" : "noindex, nofollow"
  );

  // Construir hreflang links
  const hreflangLinks = computed(() => {
    if (!shouldIndex.value) return [];
    const links = [];
    if (esUrl.value) {
      links.push({ rel: "alternate", hreflang: "es", href: esUrl.value });
      links.push({ rel: "alternate", hreflang: "x-default", href: esUrl.value });
    }
    if (enUrl.value) {
      links.push({ rel: "alternate", hreflang: "en", href: enUrl.value });
    }
    return links;
  });

  // Canonical link
  const canonicalLink = computed(() => {
    if (!canonicalUrl.value) return [];
    return [{ rel: "canonical", href: canonicalUrl.value }];
  });

  useHead({
    htmlAttrs: { lang: locale },
    title: fullTitle,
    meta: [
      { name: "description", content: pageDescription },
      { name: "robots", content: robotsContent },
      { property: "og:title", content: fullTitle },
      { property: "og:description", content: pageDescription },
      { property: "og:url", content: ogUrl },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "CNMC - Test de Velocidad" },
      { property: "og:locale", content: ogLocale },
      { property: "og:locale:alternate", content: ogLocaleAlternate },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: fullTitle },
      { name: "twitter:description", content: pageDescription },
      { name: "twitter:image", content: ogImage },
    ],
    link: computed(() => [...canonicalLink.value, ...hreflangLinks.value]),
  });
}
